import { pdf } from "@react-pdf/renderer";
import { supabase } from "../lib/supabase";
import { ShippingLabelDocument } from "../components/ShippingLabel";
import { ProductWithBarcode } from "../components/ShippingLabel";
import JsBarcode from "jsbarcode";

interface Booking {
  id: number;
  reference: string;
  transport_type: { name: string; abbreviation: string };
  booking_type?: { name: string };
  booking_incoterm: { name: string };
  origin_port?: { name: string; country: string };
  destination_port?: { name: string; country: string };
  supplier: {
    name: string;
    email: string;
    phone: string;
    country: string;
    city: string;
    address: string;
  };
}

interface Product {
  id: number;
  name: string;
  hs_code: string | null;
  gross_weight: number;
  cbm: number;
  pieces?: number | null;
  cartons?: number | null;
  ean: number | null;
  company_id: number;
}

export const generateAndSaveShippingLabel = async (
  booking: Booking,
  product: Product,
  companyName: string
): Promise<string | null> => {
  try {
    // Generate barcode if EAN exists
    let barcodeBase64 = null;
    if (product.ean) {
      try {
        // Create a canvas element
        const canvas = document.createElement("canvas");
        canvas.width = 800;
        canvas.height = 200;

        // Use the EAN number as is
        const barcodeValue = product.ean.toString();

        // Generate barcode using JsBarcode with CODE128 format
        JsBarcode(canvas, barcodeValue, {
          format: "CODE128",
          width: 2,
          height: 100,
          displayValue: false,
          fontSize: 20,
          margin: 10,
          background: "#ffffff",
          lineColor: "#000000",
          flat: true,
          textAlign: "center",
          textPosition: "bottom",
          textMargin: 6,
        });

        // Convert to base64
        barcodeBase64 = canvas.toDataURL("image/png", 1.0);
        console.log("Successfully generated barcode for:", barcodeValue);
      } catch (err) {
        console.error("Error generating barcode:", err);
      }
    }

    // Convert Product to ProductWithBarcode
    const productWithBarcode: ProductWithBarcode = {
      ...product,
      gross_weight: product.gross_weight ? Number(product.gross_weight.toFixed(2)) : 0,
      cbm: product.cbm ? Number(product.cbm.toFixed(2)) : 0,
      barcodeBase64,
    };

    // Generate the PDF blob
    const blob = await pdf(
      ShippingLabelDocument({
        booking,
        product: productWithBarcode,
        companyName,
      })
    ).toBlob();

    // Convert blob to base64
    const reader = new FileReader();
    const base64Promise = new Promise<string>((resolve) => {
      reader.onloadend = () => {
        const base64data = reader.result as string;
        resolve(base64data.split(',')[1]); // Remove the data URL prefix
      };
    });
    reader.readAsDataURL(blob);
    const base64Data = await base64Promise;

    // Create the file path with timestamp to ensure uniqueness and bypass caching
    const timestamp = Date.now();
    const filePath = `${product.company_id}/${booking.id}/booking_${booking.id}_product_${product.id}_${booking.transport_type?.abbreviation || ""}_shipping_label_${timestamp}.pdf`;
    console.log("Attempting to upload to path:", filePath);

    try {
      // Upload to Supabase storage using base64
      const { error: uploadError } = await supabase.storage
        .from("shipping_labels")
        .upload(filePath, decode(base64Data), {
          contentType: "application/pdf",
          upsert: true,
        });

      if (uploadError) {
        throw uploadError;
      }

      console.log("Successfully uploaded shipping label to:", filePath);
      return filePath;
    } catch (uploadError) {
      console.error("Error uploading shipping label:", uploadError);
      console.error("Upload error details:", {
        path: filePath,
        companyId: product.company_id,
        error: uploadError
      });
      return null;
    }
  } catch (error) {
    console.error("Error generating shipping label:", error);
    return null;
  }
};

// Helper function to decode base64
function decode(base64: string): Uint8Array {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

export function generateRegenerateLabelUrl(
  productName: string,
  pieces: number,
  cartons: number,
  weight: number,
  cbm: number,
  ean: string,
  bookingId: number,
  productId: number,
  companyId: number,
  companyName: string,
  baseUrl: string = window.location.origin
): string {
  const data = {
    productName,
    pieces,
    cartons,
    weight,
    cbm,
    ean,
    bookingId,
    productId,
    companyId,
    companyName,
  };

  // Encode the data as base64
  const encodedData = btoa(JSON.stringify(data));

  // Generate a hash using the secret key
  const secret = process.env.REACT_APP_LABEL_SECRET || "your-secret-key";
  const hash = btoa(encodedData + secret);

  // Construct the URL
  return `${baseUrl}/regenerate-label?data=${encodedData}&hash=${hash}`;
} 